import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';

// 说明：
// 1. `m` 是 Flarum core 通过 expose-loader 暴露的全局 mithril 实例，也是 JSX 的 pragma。
//    不要 `import m from 'mithril'`，否则会打包出第二份 mithril，m.redraw() 会作用在错误实例上。
// 2. Flarum 2 的模块注册表是懒加载的：SignUpModal 属于按需加载的 chunk，
//    直接 import 会在 debug 模式下抛 "No module found"。必须把模块路径字符串传给
//    extend()，由其内部通过 flarum.reg.onLoad 等模块就绪后再打补丁。
const SIGN_UP_MODAL = 'flarum/forum/components/SignUpModal';
const TURNSTILE_SCRIPT_ID = 'kapok-turnstile-script';
const RESEND_COOLDOWN = 60;

/**
 * 惰性读取 Turnstile site key。
 *
 * 注意：Flarum 2 的 boot() 先跑 initialize() 再给 app.forum 赋值，
 * 因此在初始化器里直接访问 app.forum.attribute() 会抛 TypeError 并导致
 * 整个初始化器被吞掉，必须在真正用到时才读取。
 */
function turnstileSiteKey() {
  return app.forum && app.forum.attribute('kapokEmailVerifyTurnstileSiteKey') || '';
}

/**
 * 按需加载 Cloudflare Turnstile 脚本（explicit 模式，便于在弹窗中渲染）。
 */
function loadTurnstile() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile));
      existing.addEventListener('error', () => reject(new Error('turnstile-load-failed')));
      return;
    }
    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error('turnstile-load-failed'));
    document.head.appendChild(script);
  });
}
function startCountdown(modal, seconds) {
  modal.kapokCountdown = seconds;
  if (modal.kapokTimer) clearInterval(modal.kapokTimer);
  modal.kapokTimer = setInterval(() => {
    modal.kapokCountdown -= 1;
    if (modal.kapokCountdown <= 0) {
      clearInterval(modal.kapokTimer);
      modal.kapokTimer = null;
      modal.kapokCountdown = 0;
    }
    m.redraw();
  }, 1000);
}
function sendCode(modal) {
  const siteKey = turnstileSiteKey();
  const email = (modal.email() || '').trim();
  if (!email) {
    app.alerts.show({
      type: 'error'
    }, app.translator.trans('kapok-plus-email-verify.forum.signup.email_needed'));
    return;
  }
  if (siteKey && !modal.kapokCaptchaToken) {
    app.alerts.show({
      type: 'error'
    }, app.translator.trans('kapok-plus-email-verify.forum.signup.captcha_needed'));
    return;
  }
  modal.kapokSending = true;
  app.request({
    method: 'POST',
    url: `${app.forum.attribute('apiUrl')}/kapok-email/verification-code`,
    body: {
      email,
      captchaToken: modal.kapokCaptchaToken || '',
      inviteCode: modal.kapokInvite || ''
    },
    deserialize: JSON.parse
  }).then(response => {
    const minutes = Math.max(1, Math.round((response?.expiresIn || 600) / 60));
    app.alerts.show({
      type: 'success'
    }, app.translator.trans('kapok-plus-email-verify.forum.signup.sent', {
      email,
      minutes
    }));
    startCountdown(modal, RESEND_COOLDOWN);

    // Turnstile token 一次性使用，发送后重置以便下次重新验证
    if (siteKey && window.turnstile && modal.kapokWidgetId !== null) {
      try {
        window.turnstile.reset(modal.kapokWidgetId);
      } catch (e) {
        // 忽略重置失败
      }
      modal.kapokCaptchaToken = '';
    }
  }).catch(() => {
    // 错误提示由 Flarum 默认的 request 错误处理弹出（JSON:API detail）
  }).then(() => {
    modal.kapokSending = false;
    m.redraw();
  });
}
function renderCaptcha(modal) {
  const siteKey = turnstileSiteKey();
  if (!siteKey) return;
  loadTurnstile().then(turnstile => {
    const el = document.getElementById(modal.kapokCaptchaId);
    if (!el || modal.kapokWidgetId !== null) return;
    modal.kapokWidgetId = turnstile.render(el, {
      sitekey: siteKey,
      appearance: 'interaction-only',
      callback: token => {
        modal.kapokCaptchaToken = token;
        m.redraw();
      },
      'expired-callback': () => {
        modal.kapokCaptchaToken = '';
        m.redraw();
      },
      'error-callback': () => {
        modal.kapokCaptchaToken = '';
        m.redraw();
      }
    });
  }).catch(() => {
    app.alerts.show({
      type: 'error'
    }, app.translator.trans('kapok-plus-email-verify.forum.signup.captcha_unavailable'));
  });
}
app.initializers.add('kapok-plus-email-verify', () => {
  extend(SIGN_UP_MODAL, 'oninit', function () {
    this.kapokCode = '';
    this.kapokSending = false;
    this.kapokCountdown = 0;
    this.kapokTimer = null;
    this.kapokCaptchaToken = '';
    this.kapokCaptchaId = `kapok-turnstile-${Math.random().toString(36).slice(2)}`;
    this.kapokWidgetId = null;
  });
  extend(SIGN_UP_MODAL, 'oncreate', function () {
    renderCaptcha(this);
  });
  extend(SIGN_UP_MODAL, 'onremove', function () {
    if (this.kapokTimer) clearInterval(this.kapokTimer);
    if (this.kapokWidgetId !== null && window.turnstile) {
      try {
        window.turnstile.remove(this.kapokWidgetId);
      } catch (e) {
        // 组件已随 DOM 卸载
      }
    }
  });
  extend(SIGN_UP_MODAL, 'fields', function (items) {
    // 通过第三方 OAuth token 注册时不需要邮箱验证码
    if (this.attrs.token) return;
    const siteKey = turnstileSiteKey();
    if (siteKey) {
      items.add('kapokCaptcha', m("div", {
        className: "Form-group kapokCaptcha"
      }, m("div", {
        id: this.kapokCaptchaId
      })), 16);
    }
    items.add('kapokCode', m("div", {
      className: "Form-group"
    }, m("label", {
      className: "label"
    }, app.translator.trans('kapok-plus-email-verify.forum.signup.code_label')), m("div", {
      className: "kapokCodeRow"
    }, m("input", {
      className: "FormControl kapokCodeInput",
      name: "emailVerificationCode",
      type: "text",
      inputMode: "numeric",
      autoComplete: "one-time-code",
      maxLength: "6",
      placeholder: app.translator.trans('kapok-plus-email-verify.forum.signup.code_placeholder'),
      "aria-label": app.translator.trans('kapok-plus-email-verify.forum.signup.code_label'),
      value: this.kapokCode,
      disabled: this.loading,
      oninput: e => {
        this.kapokCode = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
      }
    }), m("button", {
      type: "button",
      className: "Button Button--primary kapokSendButton",
      disabled: this.kapokSending || this.kapokCountdown > 0 || this.loading,
      onclick: () => sendCode(this)
    }, this.kapokSending ? app.translator.trans('kapok-plus-email-verify.forum.signup.sending') : this.kapokCountdown > 0 ? app.translator.trans('kapok-plus-email-verify.forum.signup.countdown', {
      seconds: this.kapokCountdown
    }) : app.translator.trans('kapok-plus-email-verify.forum.signup.send_code')))), 15);
  });
  extend(SIGN_UP_MODAL, 'submitData', function (data) {
    data.emailVerificationCode = this.kapokCode;
    return data;
  });
});